import { Center, IconButton, InputGroup, Spinner, Text } from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { useMemo, useCallback } from 'react';
import { Trans, Plural } from '@lingui/react/macro';
import { ProviderCoursesTable } from './ProviderCoursesTable';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { ArrowRightFromLine } from 'lucide-react';

import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { useParams, useSearchParams } from 'react-router-dom';
import upperFirst from 'lodash-es/upperFirst.js';
import pickBy from 'lodash-es/pickBy.js';
import { ProviderCoursesFilters } from './ProviderCoursesFilters';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import {
  CourseStatusSchema,
  type CourseStatus,
} from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import {
  CourseKindSchema,
  type CourseKind,
} from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { withPermission } from '../withPermission';
import { emptyPaginationResponse, parseSortOrder, SortOrder } from '@duke-hq/lib/pagination';
import { getCourseKindLabel } from '../lib/courseUtils';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';

import type { ProviderCourseURLParams } from './types';

export const ProviderCourses = withPermission('canViewProviders', () => {
  const { providerId } = useParams();
  const tenant = Number(providerId);
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const { currentPage, pageSize, setPage } = usePagination();

  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: ['created_at'],
    sort: [SortOrder.DESC],
    page: '1',
  });

  const querySearch = searchParams.get('search') ?? '';

  const URLFilters: ProviderCourseURLParams = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { page, search, kind, status, sort, orderBy, ...rest } = urlFilters;
    return {
      ...rest,
      orderBy: orderBy ?? 'created_at',
      sort: parseSortOrder(sort) ?? SortOrder.DESC,
      kind: CourseKindSchema.safeParse(kind).data,
      status: CourseStatusSchema.safeParse(status).data,
      page: page ?? '1',
    };
  }, [searchParams]);

  const { data: paginatedCourses, isFetching } = trpcAdmin.providers.courses.useQuery(
    {
      tenant_id: tenant,
      search: querySearch.trim(),
      filters: URLFilters,
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  const handleFilterChange = useCallback(
    (newFilters: Partial<ProviderCourseURLParams>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters },
        (value) => value != null && value != '',
      );

      if (updatedFilters.page && updatedFilters.page === '1') {
        delete updatedFilters.page;
      }

      const newSearchParams = new URLSearchParams(updatedFilters as Record<string, string>);
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const kindOptions = useMemo(() => {
    return CourseKindSchema.options.map((kind: CourseKind) => ({
      value: kind,
      label: getCourseKindLabel(kind),
    }));
  }, []);

  const statusOptions = useMemo(() => {
    return CourseStatusSchema.options.map((status: CourseStatus) => ({
      value: status,
      label: upperFirst(status.replace(/_/g, ' ').toLowerCase()),
    }));
  }, []);

  return (
    <TableViewLayout
      pageHeader={[
        !isExpanded ? (
          <IconButton
            aria-label="expand secondary sidebar"
            icon={<ArrowRightFromLine />}
            variant="tertiary"
            onClick={toggleExpandSubSidebar}
            size="md"
          />
        ) : null,
        <PageHeading key="courses">
          <Trans>Courses</Trans>
        </PageHeading>,
      ]}
      filters={
        <>
          <ProviderCoursesFilters
            filters={URLFilters}
            statusOptions={statusOptions}
            kindOptions={kindOptions}
            onChange={handleFilterChange}
          />
          <InputGroup size="sm">
            <SearchInput maxW="search-input" />
          </InputGroup>
        </>
      }
      tableActionsLeft={
        <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
          <Plural
            value={paginatedCourses.pagination.totalCount || 0}
            zero="0 courses"
            one="1 course"
            other="# courses"
          />
        </Text>
      }
      table={
        isFetching ? (
          <Center p={8}>
            <Spinner size="xl" transitionDelay={'2900ms'} />
          </Center>
        ) : paginatedCourses.items.length > 0 ? (
          <ProviderCoursesTable
            courses={paginatedCourses.items}
            urlParams={URLFilters}
            onFilters={handleFilterChange}
          />
        ) : (
          <Center p={8}>
            <Text textAlign="center">
              <Trans>No courses found</Trans>
            </Text>
          </Center>
        )
      }
      pagination={
        paginatedCourses.items.length > 0 ? (
          <Pagination pagination={paginatedCourses.pagination} onPageChange={setPage} />
        ) : null
      }
    />
  );
});
